import type { ColDef, ColTypeDef, GetRowIdParams, GridApi, GridReadyEvent } from 'ag-grid-community';
import { MasterDetailModule } from 'ag-grid-enterprise';
import { quotesBlotterApiConnector, ServerSideBlotterSearchRequest } from 'apiConnector/blotters';
import { PageLessFooter } from 'components/Blotters/pageless-footer';
import { useQuotesBlotterServerSide } from 'components/Blotters/Quotes/Grid/hooks-serverside/use-quotes-blotter';
import { useTranslateRequest } from 'components/Blotters/Quotes/Grid/hooks-serverside/use-translate-request';
import { ExternalFilter } from 'components/Blotters/Quotes/Grid/model';
import { SensitivityDetailCellRenderer } from 'components/Blotters/Quotes/Grid/quote-blotter-sensitivity-details';
import { AddToOrderBasketCellRenderer } from 'components/Forms/OrdersBasket/AddToOrderBasketButton';
import { getStatusFieldColor } from 'components/shared/Blotter/BlotterPanel/Fields/StatusField/StatusField';
import { ServerSideBlotterGrid } from 'components/shared/Blotter/Grid/blotter-serverside';
import { cellTranslatorRenderer } from 'components/shared/Blotter/Grid/CellRenderers/cellTranslatorFactory';
import { useContextMenuItems } from 'components/shared/Blotter/Grid/Hooks/useContextMenuItems';
import { useDefaultColumnDefinition } from 'components/shared/Blotter/Grid/Hooks/useDefaultColumnDefinition';
import { useGridAutoRefresh } from 'components/shared/Blotter/Grid/Hooks/useGridAutoRefresh';
import { useServerSideQuoteModeColumnType } from 'components/shared/Blotter/Grid/Hooks/useQuoteModeColumn';
import { useServerSideBlotterDataSource } from 'components/shared/Blotter/Grid/Hooks/useServerSideDatasource';
import { useServerSideDefaultColumnTypes } from 'components/shared/Blotter/Grid/Hooks/useServerSideDefaultColumnTypes';
import { useServerSideStatusColumnType } from 'components/shared/Blotter/Grid/Hooks/useStatusColumnType';
import { useServerSideTradeAuthorizationStatusColumnType } from 'components/shared/Blotter/Grid/Hooks/useTradeAuthorizationStatusColumnType';
import {
  useGetFilterValuesWithApi,
  useTranslateFilterInColumnDefinitions,
} from 'components/shared/Blotter/Grid/Hooks/useTranslateFilterInColumnDefinitions';
import { canSetActive, getHeaderForBlotterIntlKey } from 'components/shared/Blotter/utils';
import { IBlotterQuoteItem } from 'models/IBlotterItem';
import { useCallback, useMemo, useState } from 'react';
import { httpClient } from 'utils/http/httpClient';

const getHeaderIntlKey = getHeaderForBlotterIntlKey('quoteBlotter');

const technicalColumnDefinition: Partial<ColDef> = {
  resizable: false,
  headerValueGetter: () => '',
  suppressHeaderMenuButton: true,
  suppressColumnsToolPanel: true,
  floatingFilter: false,
  sortable: false,
  pinned: true,
  lockPinned: true,
  lockPosition: 'right',
  lockVisible: true,
};
const modules = [MasterDetailModule];

export type QuotesBlotterServerSideGridProps = {};

const stringWithMasterDetail = {
  ...technicalColumnDefinition,
  valueFormatter: () => '',
  width: 65,
  minWidth: 65,
  cellRenderer: 'agGroupCellRenderer',
} as ColTypeDef<IBlotterQuoteItem>;

const addToOrderBasket = {
  cellRenderer: AddToOrderBasketCellRenderer,
  width: 50,
  minWidth: 50,
  ...technicalColumnDefinition,
} as ColTypeDef<IBlotterQuoteItem>;

function getRowId({ data }: GetRowIdParams) {
  return data['quoteId'];
}

const quoteBlotterApi = quotesBlotterApiConnector(httpClient);
const searchItems = (req: ServerSideBlotterSearchRequest) => quoteBlotterApi.serverSideSearch(req);

type Props = { setLastBlotterRequest: (value: ServerSideBlotterSearchRequest) => void };

const MaxItemsCount = 10_000;

export function QuotesBlotterServerSideGrid({ setLastBlotterRequest }: Props) {
  const getFilterValuesWithDispatch = useGetFilterValuesWithApi(quoteBlotterApi);
  const defaultColumnDef = useDefaultColumnDefinition<IBlotterQuoteItem>(getHeaderIntlKey);
  const quoteModeColumn = useServerSideQuoteModeColumnType<IBlotterQuoteItem>();
  const statusColumn = useServerSideStatusColumnType<IBlotterQuoteItem>(getStatusFieldColor);
  const tradeAuthorizationColumn = useServerSideTradeAuthorizationStatusColumnType<IBlotterQuoteItem>();
  const [gridApi, setGridApi] = useState<GridApi>();
  const onAgGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);
  const getContextMenuItems = useContextMenuItems('Blotter My Quotes', gridApi);

  const customColumns = useMemo<Record<string, ColTypeDef<IBlotterQuoteItem>>>(
    () => ({
      quoteMode: quoteModeColumn,
      status: statusColumn,
      tradeAuthorizationStatus: tradeAuthorizationColumn,
      addToOrderBasket,
      stringToTranslate: {
        cellRenderer: cellTranslatorRenderer(),
      },
      stringWithMasterDetail,
    }),
    [quoteModeColumn, statusColumn, tradeAuthorizationColumn],
  );

  const columnTypes = useServerSideDefaultColumnTypes<IBlotterQuoteItem>(customColumns, 6);

  const {
    blotterRequest,
    onItemsReceived,
    onItemsRequested,
    onRequestFailed,
    isBlotterRefreshable,
    columnDefinitions,
    onBlotterColumnStateChanged,
    onPageSizeChanged,
    columnState,
    ...quoteBlotterState
  } = useQuotesBlotterServerSide();

  useGridAutoRefresh(isBlotterRefreshable ? gridApi : null);

  const columnsDefinitionsWithTranslatedFilter = useTranslateFilterInColumnDefinitions(
    columnDefinitions,
    getFilterValuesWithDispatch,
  );
  const translateRequest = useTranslateRequest(columnDefinitions);
  const searchServerSideBlotterItems = useCallback(
    (req: ServerSideBlotterSearchRequest) => {
      setLastBlotterRequest(req);
      return searchItems(req);
    },
    [setLastBlotterRequest],
  );
  const serverSideDatasource = useServerSideBlotterDataSource<
    ServerSideBlotterSearchRequest,
    ExternalFilter,
    IBlotterQuoteItem,
    Error
  >({
    blotterRequest,
    searchItems: searchServerSideBlotterItems,
    translateRequest,
    onItemsRequested,
    onItemsReceived,
    onRequestFailed,
    isNativePaginationEnabled: true,
  });

  const getIsRowMaster = useCallback(
    (data: IBlotterQuoteItem) => data.status === 'Traded' || data.status === 'Quoted',
    [],
  );

  const [isTruncated, setIsTruncated] = useState(false);

  return (
    <>
      <ServerSideBlotterGrid<ServerSideBlotterSearchRequest, IBlotterQuoteItem>
        columnDefinitions={columnsDefinitionsWithTranslatedFilter}
        {...quoteBlotterState}
        columnState={columnState}
        name="quoteBlotter"
        logAnalyticName="Blotter My Quotes"
        isRowMaster={getIsRowMaster}
        masterDetail
        keepDetailRows
        detailCellRenderer={SensitivityDetailCellRenderer}
        detailRowAutoHeight
        columnTypes={columnTypes}
        defaultColDef={defaultColumnDef}
        e2e="quote-blotter"
        canSetActive={canSetActive}
        getContextMenuItems={getContextMenuItems}
        getRowId={getRowId}
        onGridReady={onAgGridReady}
        modules={modules}
        serverSideDatasource={serverSideDatasource}
        onBlotterColumnStateChanged={onBlotterColumnStateChanged}
        onPageSizeChanged={onPageSizeChanged}
        useNativePagination
        maxRowsCount={10_000}
        setIsTruncated={setIsTruncated}
      />
      <PageLessFooter maxItemsCount={MaxItemsCount} isTruncated={isTruncated}></PageLessFooter>
    </>
  );
}

QuotesBlotterServerSideGrid.displayName = 'QuotesBlotterServerSideGrid';
